import type { FC, SVGProps } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n';

type Props = {
  telegramUrl?: string | null;
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  messengerUrl?: string | null;
  active?: boolean;
  socialLayout?: 'default' | 'menuGrid';
};

type LinkItem = {
  key: string;
  label: string;
  url: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  accentClassName: string;
  iconWrapClassName: string;
  iconClassName: string;
};

const TelegramIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M21.4 4.6c.4-.2.8.2.7.7l-3 14.14c-.1.46-.63.69-1.03.46l-4.83-2.81-2.47 2.38c-.35.34-.95.1-.95-.39v-3.49l8.22-7.42c.28-.25-.05-.68-.37-.46l-9.96 6.28-4.38-1.37c-.5-.16-.54-.86-.06-1.08L21.4 4.6Z"
    />
  </svg>
);

const InstagramIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <rect x="3.25" y="3.25" width="17.5" height="17.5" rx="5.25" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17.2" cy="6.8" r="1.15" fill="currentColor" />
  </svg>
);

const TikTokIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="#25F4EE"
      d="M13.7 3.2c.28 1.75 1.18 3.18 2.94 3.93.56.23 1.13.36 1.76.39v2.37a7.9 7.9 0 0 1-3.38-.87v5.16a5.26 5.26 0 1 1-4.52-5.2v2.58a2.65 2.65 0 1 0 1.82 2.52V3.2h1.38Z"
    />
    <path
      fill="#FE2C55"
      d="M14.38 2.5c.28 1.82 1.25 3.3 3.14 4.07.56.23 1.16.35 1.79.37v2.8a8.52 8.52 0 0 1-3.77-.93v5.09a5.92 5.92 0 1 1-5.09-5.86v2.86a3.11 3.11 0 1 0 2.12 2.95V2.5h1.81Z"
      opacity="0.96"
    />
    <path
      fill="#FFFFFF"
      d="M14.88 3.5c.28 1.82 1.25 3.3 3.14 4.07.56.23 1.16.35 1.79.37v2.8a8.52 8.52 0 0 1-3.77-.93v5.09a5.92 5.92 0 1 1-5.09-5.86v2.86a3.11 3.11 0 1 0 2.12 2.95V3.5h1.81Z"
    />
  </svg>
);

const FacebookIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M13.39 20.5v-6.62h2.22l.33-2.58h-2.55V9.66c0-.75.2-1.26 1.28-1.26h1.37V6.09c-.24-.03-1.05-.09-2-.09-1.98 0-3.33 1.2-3.33 3.42v1.88H8.5v2.58h2.2v6.62h2.69Z"
    />
  </svg>
);

const StoreSocialButtons = ({
  telegramUrl,
  instagramUrl,
  tiktokUrl,
  messengerUrl,
  active = false,
  socialLayout = 'default',
}: Props) => {
  const { t } = useI18n();
  const contactItem: LinkItem | null = telegramUrl
    ? {
        key: 'telegram',
        label: 'Inquiry & Contact',
        url: telegramUrl,
        icon: TelegramIcon,
        accentClassName: active
          ? 'border-sky-300/60 bg-sky-400/15 text-white hover:bg-sky-400/25'
          : 'border-sky-200 bg-sky-50 text-sky-950 hover:bg-sky-100',
        iconWrapClassName: active ? 'bg-white/15 text-white' : 'bg-sky-500 text-white',
        iconClassName: 'h-6 w-6',
      }
    : null;

  const socialItems: LinkItem[] = [
    instagramUrl
      ? {
          key: 'instagram',
          label: 'Instagram',
          url: instagramUrl,
          icon: InstagramIcon,
          accentClassName: active
            ? 'border-fuchsia-300/60 bg-fuchsia-400/15 text-white hover:bg-fuchsia-400/25'
            : 'border-rose-200 bg-gradient-to-br from-rose-50 via-orange-50 to-fuchsia-50 text-rose-950 hover:from-rose-100 hover:via-orange-100 hover:to-fuchsia-100',
          iconWrapClassName: active
            ? 'bg-white/15 text-white'
            : 'bg-gradient-to-br from-fuchsia-600 via-rose-500 to-orange-400 text-white',
          iconClassName: 'h-8 w-8',
        }
      : null,
    tiktokUrl
      ? {
          key: 'tiktok',
          label: 'TikTok',
          url: tiktokUrl,
          icon: TikTokIcon,
          accentClassName: active
            ? 'border-slate-300/60 bg-white/10 text-white hover:bg-white/15'
            : 'border-slate-200 bg-[linear-gradient(135deg,#eef2ff,#f8fafc)] text-slate-950 hover:bg-[linear-gradient(135deg,#e7ecff,#f4f7fb)]',
          iconWrapClassName: active
            ? 'bg-white/15 text-[#63fff3]'
            : 'bg-[#111827]',
          iconClassName: 'h-8 w-8',
        }
      : null,
    messengerUrl
      ? {
          key: 'messenger',
          label: 'Facebook',
          url: messengerUrl,
          icon: FacebookIcon,
          accentClassName: active
            ? 'border-blue-300/60 bg-blue-400/15 text-white hover:bg-blue-400/25'
            : 'border-blue-200 bg-blue-50 text-blue-950 hover:bg-blue-100',
          iconWrapClassName: active
            ? 'bg-white/15 text-white'
            : 'bg-[#1877f2] text-white',
          iconClassName: 'h-8 w-8',
        }
      : null,
  ].filter((item): item is LinkItem => item !== null);

  if (!contactItem && socialItems.length === 0) return null;

  const ContactIcon = contactItem?.icon;

  return (
    <div className={['mt-3 border-t pt-3', active ? 'border-white/20' : 'border-slate-200/80'].join(' ')}>
      <div className="space-y-2.5">
        {contactItem ? (
          <a
            href={contactItem.url}
            target="_blank"
            rel="noreferrer"
            className={[
              'group flex min-h-[58px] items-center gap-3 rounded-[18px] border px-3.5 py-3 transition',
              'shadow-sm',
              contactItem.accentClassName,
            ].join(' ')}
            aria-label={t('common.openExternal', { label: contactItem.label })}
          >
            {ContactIcon ? (
              <div className={['flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px]', contactItem.iconWrapClassName].join(' ')}>
                <ContactIcon className={contactItem.iconClassName} />
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold leading-5">{contactItem.label}</div>
            </div>
            <ArrowUpRight className={['h-4.5 w-4.5 shrink-0 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5', active ? 'text-white/80' : 'text-slate-400'].join(' ')} />
          </a>
        ) : null}

        {socialItems.length > 0 ? (
          socialLayout === 'menuGrid' ? (
            <div className="grid grid-cols-3 gap-2.5">
              {socialItems.map(({ key, label, url, icon: Icon, iconWrapClassName, iconClassName }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex min-h-[92px] flex-col items-center justify-center rounded-[18px] border border-white/80 bg-white/95 px-2 py-3 text-center shadow-[0_12px_24px_rgba(15,23,42,0.07)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_28px_rgba(15,23,42,0.1)]"
                  aria-label={t('common.openExternal', { label })}
                >
                  <div className={['flex h-14 w-14 items-center justify-center rounded-[16px] text-slate-900 transition', iconWrapClassName].join(' ')}>
                    <Icon className={iconClassName} />
                  </div>
                  <div className="mt-2 text-[11px] font-semibold leading-[1.2] text-slate-900">
                    {label}
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {socialItems.map(({ key, label, url, icon: Icon, accentClassName, iconWrapClassName, iconClassName }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className={[
                    'group flex h-11 w-11 items-center justify-center rounded-[14px] border transition',
                    'shadow-sm',
                    accentClassName,
                  ].join(' ')}
                  aria-label={t('common.openExternal', { label })}
                >
                  <div className={['flex h-8 w-8 items-center justify-center rounded-[10px]', iconWrapClassName].join(' ')}>
                    <Icon className={iconClassName} />
                  </div>
                </a>
              ))}
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

export default StoreSocialButtons;
